import React from 'react';

import { Box, Typography, IconButton, Divider } from '@mui/material';
import _ from 'lodash';

import { useSearchCodesQuery } from '../../../common/apis/FlorenceApi';
import { ReactComponent as CancelIconBlue } from '../../../common/assets/icons/cancel_icon_blue.svg';
import { Code, SelectableCode } from '../../../common/types/Codes';

import AddCodeTable from './AddCodeTable';
import AddCodeTextField from './AddCodeTextField';

type Props = {
  isClinicNote: boolean;
  selectedCodes: SelectableCode[];
  type: 'CPT' | 'HCPCS' | 'ICD10';
  addManualCode?: (code: SelectableCode) => void;
  onClickClose: () => void;
};

const AddCodeContainer: React.FC<Props> = ({
  isClinicNote = false,
  selectedCodes,
  type,
  addManualCode,
  onClickClose,
}) => {
  const [value, setValue] = React.useState('');
  const [order, setOrder] = React.useState(100);
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  const { data: codes, isFetching } = useSearchCodesQuery(
    {
      text: debouncedValue,
      type: type,
    },
    {
      skip: debouncedValue.length < 2,
    }
  );

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [value]);

  const isSelected = (code: Code) => {
    return selectedCodes.findIndex((c) => c.code === code.code) !== -1;
  };

  const didSelectCode = (code: Code) => {
    const codeToAdd =
      type === 'CPT'
        ? {
            ...code,
            modifiers: [],
            multiplier: 1,
            manuallyAdded: true,
            selected: !isSelected(code),
            order,
            isPrimary: true,
            icd10: [],
          }
        : {
            id: Math.random().toString(),
            code: code.code,
            description: code.description,
            modifiers: [],
            multiplier: 1,
            manuallyAdded: true,
            selected: !isSelected(code),
            order,
            workRVU: '',
            totalRVU: '',
            facilityRVU: '',
            nonFacilityRVU: '',
            icd10: [],
          };

    if (addManualCode) {
      addManualCode(codeToAdd);
      setOrder(order - 1);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: '80%',
        maxHeight: '80%',
        minHeight: '455px',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flex: 1,
        borderRadius: '15px',
        padding: '30px',
        flexDirection: 'column',
      }}
    >
      <Box
        className='row'
        sx={{ justifyContent: 'center', position: 'relative' }}
      >
        <Typography
          sx={{ alignSelf: 'center', mb: '35px' }}
          color='text.primary'
          variant='h5'
        >
          Add {type} code
        </Typography>
        <IconButton
          onClick={() => {
            onClickClose();
            setValue('');
          }}
          sx={{ top: -3, right: 0, position: 'absolute' }}
        >
          <CancelIconBlue />
        </IconButton>
      </Box>
      <AddCodeTextField
        isFetching={isFetching}
        value={value}
        setValue={setValue}
      />
      <Divider sx={{ mb: '56px', mx: '-30px' }} />
      {!_.isEmpty(value) && codes && (
        <>
          <Typography
            color='primary'
            sx={{
              fontSize: '14px',
              fontFamily: 'SFProDisplay-Bold',
              mb: '16px',
              ml: '5px',
            }}
          >
            {_.isEmpty(value)
              ? ''
              : `${`Found ${!!!codes ? '0' : codes.length} matches`}`}
          </Typography>
          <AddCodeTable
            onClose={onClickClose}
            selectedCodes={selectedCodes}
            codes={_.isEmpty(value) ? [] : codes ?? []}
            didSelectCode={didSelectCode}
            isClinicNote={isClinicNote}
            type={type}
          />
        </>
      )}
    </Box>
  );
};

export default AddCodeContainer;
